import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth, API } from '../App';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ArrowLeft, Mic, MicOff, Volume2, VolumeX, Play, 
  CheckCircle2, Clock, Target, Award, TrendingUp,
  AlertTriangle, ChevronRight, RefreshCw, Home, BarChart3,
  Brain, Lightbulb, Star, Loader2, SkipForward, User
} from 'lucide-react';

// Interview stages
const STAGES = {
  LOADING: 'loading',
  CREDIT_CHECK: 'credit_check',
  ACKNOWLEDGMENT: 'acknowledgment', // New stage for credit warning
  MIC_TEST: 'mic_test',
  INTRO: 'intro',
  READING: 'reading',
  COUNTDOWN: 'countdown',
  ANSWERING: 'answering',
  PROCESSING: 'processing',
  RESULTS: 'results'
};

// Professional AI Interviewer Avatar Component
function AIAvatar({ speaking, size = 'large' }) {
  const sizeClasses = size === 'large' ? 'w-36 h-36' : 'w-24 h-24';
  const innerSize = size === 'large' ? 'w-32 h-32' : 'w-20 h-20';
  const iconSize = size === 'large' ? 'w-16 h-16' : 'w-10 h-10';
  
  return (
    <div className={`relative ${sizeClasses} mx-auto`}>
      {/* Outer glow rings when speaking */}
      {speaking && (
        <>
          <motion.div
            className="absolute inset-0 rounded-full bg-gradient-to-r from-red-500/20 to-amber-500/20"
            animate={{ scale: [1, 1.2, 1], opacity: [0.6, 0.2, 0.6] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className="absolute inset-0 rounded-full bg-gradient-to-r from-red-500/10 to-amber-500/10"
            animate={{ scale: [1, 1.4, 1], opacity: [0.4, 0, 0.4] }}
            transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 0.3 }}
          />
        </>
      )}
      
      {/* Avatar container with professional design */}
      <motion.div 
        className={`relative ${sizeClasses} rounded-full bg-gradient-to-br from-slate-700 via-slate-800 to-slate-900 p-1 shadow-2xl border-2 border-slate-600`}
        animate={speaking ? { scale: [1, 1.02, 1] } : {}}
        transition={{ duration: 0.8, repeat: speaking ? Infinity : 0, ease: "easeInOut" }}
      >
        <div className={`${innerSize} rounded-full bg-gradient-to-br from-slate-100 via-slate-50 to-white flex items-center justify-center overflow-hidden relative`}>
          {/* Professional silhouette avatar */}
          <div className="relative flex flex-col items-center justify-center">
            {/* Head silhouette */}
            <div className={`${size === 'large' ? 'w-12 h-12' : 'w-8 h-8'} rounded-full bg-gradient-to-br from-slate-600 to-slate-700 mb-0.5`} />
            {/* Shoulders silhouette */}
            <div className={`${size === 'large' ? 'w-20 h-8' : 'w-14 h-5'} bg-gradient-to-br from-slate-600 to-slate-700 rounded-t-full -mt-2`} />
          </div>
          
          {/* Speaking wave overlay */}
          {speaking && (
            <motion.div 
              className="absolute inset-0 bg-gradient-to-t from-red-500/20 to-transparent"
              animate={{ opacity: [0.3, 0.6, 0.3] }}
              transition={{ duration: 0.5, repeat: Infinity }}
            />
          )}
        </div>
      </motion.div>
      
      {/* Status indicator badge */}
      <motion.div 
        className={`absolute -bottom-1 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-medium shadow-lg ${
          speaking 
            ? 'bg-gradient-to-r from-red-500 to-amber-500 text-white' 
            : 'bg-slate-700 text-slate-300'
        }`}
        animate={speaking ? { scale: [1, 1.05, 1] } : {}}
        transition={{ duration: 0.5, repeat: speaking ? Infinity : 0 }}
      >
        {speaking ? (
          <span className="flex items-center gap-1.5">
            <motion.span 
              className="w-1.5 h-1.5 rounded-full bg-white"
              animate={{ opacity: [1, 0.3, 1] }}
              transition={{ duration: 0.4, repeat: Infinity }}
            />
            Speaking
          </span>
        ) : (
          'AI Interviewer'
        )}
      </motion.div>
    </div>
  );
}

function InterviewRoom() {
  const navigate = useNavigate();
  const { proposalId } = useParams();
  const auth = useAuth();
  const token = auth.token;
  
  // State
  const [stage, setStage] = useState(STAGES.LOADING);
  const [sessionId, setSessionId] = useState(null);
  const [config, setConfig] = useState(null);
  const [creditStatus, setCreditStatus] = useState(null);
  const [proposalTitle, setProposalTitle] = useState('');
  const [interviewerName, setInterviewerName] = useState('Sarah');
  const [interviewerIntro, setInterviewerIntro] = useState('');
  
  // Question state
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [totalQuestions, setTotalQuestions] = useState(10);
  
  // Timer state
  const [readTimer, setReadTimer] = useState(30);
  const [countdownTimer, setCountdownTimer] = useState(3);
  const [answerTimer, setAnswerTimer] = useState(180);
  
  // Audio state
  const [micPermission, setMicPermission] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const [transcript, setTranscript] = useState('');
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  
  // Results state
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Refs
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const recognitionRef = useRef(null);
  const speechSynthRef = useRef(null);

  // Initialize on mount
  useEffect(() => {
    async function initialize() {
      try {
        // Get config
        const configRes = await axios.get(API + '/mock-interview/config');
        setConfig(configRes.data);
        
        // Check credits
        const creditRes = await axios.get(API + '/mock-interview/credits', {
          headers: { Authorization: 'Bearer ' + token }
        });
        setCreditStatus(creditRes.data);
        
        if (creditRes.data.has_credit) {
          setStage(STAGES.ACKNOWLEDGMENT);
        } else {
          setStage(STAGES.CREDIT_CHECK);
        }
      } catch (err) {
        console.error('Failed to initialize:', err);
        toast.error('Failed to load interview. Please try again.');
        navigate('/dashboard');
      }
    }
    
    initialize();
    
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      if (speechSynthesis.speaking) {
        speechSynthesis.cancel();
      }
    };
  }, [token, navigate]);

  // Text-to-Speech function
  function speak(text, onEnd) {
    if (!voiceEnabled || !('speechSynthesis' in window)) {
      if (onEnd) onEnd();
      return;
    }
    
    // Cancel any ongoing speech
    speechSynthesis.cancel();
    
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 0.95;
    utterance.pitch = 1.1;
    utterance.volume = 1;
    
    // Try to get a female UK English voice
    const voices = speechSynthesis.getVoices();
    const preferredVoice = voices.find(v => 
      v.name.includes('Female') && v.lang.includes('en-GB')
    ) || voices.find(v => 
      v.lang.includes('en-GB')
    ) || voices.find(v => 
      v.lang.includes('en')
    );
    
    if (preferredVoice) {
      utterance.voice = preferredVoice;
    }
    
    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => {
      setIsSpeaking(false);
      if (onEnd) onEnd();
    };
    utterance.onerror = () => {
      setIsSpeaking(false);
      if (onEnd) onEnd();
    };
    
    speechSynthRef.current = utterance;
    speechSynthesis.speak(utterance);
  }

  async function purchaseCredit() {
    setLoading(true);
    try {
      await axios.post(API + '/mock-interview/purchase', {}, {
        headers: { Authorization: 'Bearer ' + token }
      });
      toast.success('Interview credit purchased!');
      setCreditStatus({ ...creditStatus, has_credit: true });
      setStage(STAGES.ACKNOWLEDGMENT);
    } catch (err) {
      console.error('Purchase failed:', err);
      toast.error('Failed to purchase credit');
    } finally {
      setLoading(false);
    }
  }
  
  function acknowledgeAndProceed() {
    setStage(STAGES.MIC_TEST);
  }

  async function requestMicPermission() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      
      // Setup audio analysis for level meter
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();
      const source = audioContextRef.current.createMediaStreamSource(stream);
      source.connect(analyserRef.current);
      analyserRef.current.fftSize = 256;
      
      // Monitor audio level
      const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
      const updateLevel = () => {
        if (analyserRef.current && stage === STAGES.MIC_TEST) {
          analyserRef.current.getByteFrequencyData(dataArray);
          const avg = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
          setAudioLevel(avg);
          requestAnimationFrame(updateLevel);
        }
      };
      updateLevel();
      
      setMicPermission(true);
      toast.success('Microphone connected!');
    } catch (err) {
      console.error('Mic permission denied:', err);
      toast.error('Microphone access is required for the interview');
    }
  }

  async function startInterview() {
    setLoading(true);
    try {
      // Start session
      const startRes = await axios.post(API + '/mock-interview/start', {
        proposal_id: proposalId
      }, {
        headers: { Authorization: 'Bearer ' + token }
      });
      
      setSessionId(startRes.data.session_id);
      setProposalTitle(startRes.data.proposal_title);
      setInterviewerName(startRes.data.interviewer_name || 'Sarah');
      setInterviewerIntro(startRes.data.interviewer_intro || '');
      setTotalQuestions(startRes.data.question_count);
      
      // Complete mic test
      const micRes = await axios.post(
        API + '/mock-interview/mic-test-complete/' + startRes.data.session_id,
        {},
        { headers: { Authorization: 'Bearer ' + token } }
      );
      
      setCurrentQuestion({
        question: micRes.data.question,
        category: micRes.data.category
      });
      setCurrentQuestionIndex(0);
      
      // Show intro stage
      setStage(STAGES.INTRO);
      setLoading(false);
      
      // Speak intro, then transition to first question
      speak(startRes.data.interviewer_intro, () => {
        setTimeout(() => {
          setStage(STAGES.READING);
          setReadTimer(config.read_time_seconds);
          speakQuestion(micRes.data.question, 1); // Pass question number 1 for first question
          startReadingTimer();
        }, 1000);
      });
      
    } catch (err) {
      console.error('Failed to start interview:', err);
      toast.error('Failed to start interview');
      setLoading(false);
    }
  }

  function speakQuestion(questionText, questionNumber) {
    // Use the passed question number to ensure voice matches display
    const qNum = questionNumber !== undefined ? questionNumber : currentQuestionIndex + 1;
    const prefix = qNum === 1 
      ? "Your first question. " 
      : qNum === 2 
        ? "Question two. " 
        : `Question ${qNum}. `;
    speak(prefix + questionText);
  }

  function startReadingTimer() {
    let timeLeft = config.read_time_seconds;
    timerRef.current = setInterval(() => {
      timeLeft -= 1;
      setReadTimer(timeLeft);
      
      if (timeLeft <= 0) {
        clearInterval(timerRef.current);
        startCountdown();
      }
    }, 1000);
  }

  function skipToAnswer() {
    // Stop any ongoing speech
    if (speechSynthesis.speaking) {
      speechSynthesis.cancel();
    }
    clearInterval(timerRef.current);
    startCountdown();
  }

  function startCountdown() {
    setIsSpeaking(false);
    setStage(STAGES.COUNTDOWN);
    let count = config.countdown_seconds;
    setCountdownTimer(count);
    
    timerRef.current = setInterval(() => {
      count -= 1;
      setCountdownTimer(count);
      
      if (count <= 0) {
        clearInterval(timerRef.current);
        startAnswering();
      }
    }, 1000);
  }

  function startAnswering() {
    setStage(STAGES.ANSWERING);
    setAnswerTimer(config.answer_time_seconds);
    setTranscript('');
    startRecording();
    
    let timeLeft = config.answer_time_seconds;
    timerRef.current = setInterval(() => {
      timeLeft -= 1;
      setAnswerTimer(timeLeft);
      
      if (timeLeft <= 0) {
        clearInterval(timerRef.current);
        autoSubmitAnswer();
      }
    }, 1000);
  }

  function startRecording() {
    if (!streamRef.current) return;
    
    audioChunksRef.current = [];
    
    try {
      // Start MediaRecorder
      mediaRecorderRef.current = new MediaRecorder(streamRef.current);
      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };
      mediaRecorderRef.current.start(1000);
      setIsRecording(true);
      
      // Start Speech Recognition
      if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        recognitionRef.current = new SpeechRecognition();
        recognitionRef.current.continuous = true;
        recognitionRef.current.interimResults = true;
        recognitionRef.current.lang = 'en-GB';
        
        recognitionRef.current.onresult = (event) => {
          let finalTranscript = '';
          
          for (let i = event.resultIndex; i < event.results.length; i++) {
            const result = event.results[i];
            if (result.isFinal) {
              finalTranscript += result[0].transcript + ' ';
            }
          }
          
          setTranscript(prev => prev + finalTranscript);
        };
        
        recognitionRef.current.onerror = (err) => {
          console.log('Speech recognition error:', err);
        };
        
        recognitionRef.current.start();
      }
    } catch (err) {
      console.error('Recording error:', err);
    }
  }

  function stopRecording() {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    setIsRecording(false);
  }

  async function autoSubmitAnswer() {
    stopRecording();
    
    const answerDuration = config.answer_time_seconds - answerTimer;
    
    // Store answer
    const answerData = {
      question_index: currentQuestionIndex,
      transcript: transcript,
      duration_seconds: answerDuration
    };
    
    setAnswers(prev => [...prev, answerData]);
    
    // Submit to backend
    try {
      const res = await axios.post(API + '/mock-interview/submit-answer', {
        session_id: sessionId,
        question_index: currentQuestionIndex,
        transcript: transcript,
        duration_seconds: answerDuration
      }, {
        headers: { Authorization: 'Bearer ' + token }
      });
      
      if (res.data.is_complete) {
        // Interview complete - get assessment
        await getAssessment();
      } else {
        // Load next question
        setCurrentQuestionIndex(res.data.next_question_index);
        setCurrentQuestion({
          question: res.data.next_question,
          category: res.data.next_category
        });
        setTranscript('');
        setStage(STAGES.READING);
        setReadTimer(config.read_time_seconds);
        // Pass the next question number (1-indexed for display)
        speakQuestion(res.data.next_question, res.data.next_question_index + 1);
        startReadingTimer();
      }
    } catch (err) {
      console.error('Failed to submit answer:', err);
      toast.error('Failed to submit answer');
    }
  }

  function manualSubmitAnswer() {
    clearInterval(timerRef.current);
    autoSubmitAnswer();
  }

  async function getAssessment() {
    setStage(STAGES.PROCESSING);
    
    // Speak transition message
    speak("Thank you for completing the interview. I'm now analyzing your responses. Please wait while I prepare your assessment.");
    
    try {
      const res = await axios.post(
        API + '/mock-interview/complete/' + sessionId,
        {},
        { headers: { Authorization: 'Bearer ' + token } }
      );
      
      setAssessment(res.data.assessment);
      setStage(STAGES.RESULTS);
    } catch (err) {
      console.error('Failed to get assessment:', err);
      toast.error('Failed to process assessment');
    }
  }

  function formatTime(seconds) {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins.toString().padStart(2, '0') + ':' + secs.toString().padStart(2, '0');
  }

  function getScoreColor(score, max = 10) {
    const percentage = (score / max) * 100;
    if (percentage >= 80) return 'text-green-500';
    if (percentage >= 60) return 'text-yellow-500';
    if (percentage >= 40) return 'text-orange-500';
    return 'text-red-500';
  }

  function getReadinessColor(readiness) {
    const colors = {
      'HIGHLY READY': 'bg-green-500',
      'READY': 'bg-green-400',
      'PROMISING': 'bg-yellow-500',
      'NEEDS IMPROVEMENT': 'bg-orange-500',
      'NOT READY': 'bg-red-500'
    };
    return colors[readiness] || 'bg-slate-500';
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-red-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl" />
      </div>

      {/* Header */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-900/80 backdrop-blur-md border-b border-slate-700/50">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/dashboard" className="text-slate-400 hover:text-white transition-colors">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-amber-500 flex items-center justify-center shadow-lg">
                  <Mic className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="font-bold text-white">Mock Interview</h1>
                  {proposalTitle && <p className="text-xs text-slate-400">{proposalTitle}</p>}
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Voice toggle */}
              <button
                onClick={() => setVoiceEnabled(!voiceEnabled)}
                className={`p-2 rounded-lg transition-colors ${voiceEnabled ? 'bg-slate-700 text-white' : 'bg-slate-800 text-slate-500'}`}
                title={voiceEnabled ? 'Disable voice' : 'Enable voice'}
              >
                {voiceEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
              </button>
              
              {(stage === STAGES.READING || stage === STAGES.ANSWERING) && (
                <Badge className="bg-red-500/20 text-red-400 border-red-500/30 px-4 py-2">
                  <Clock className="w-4 h-4 mr-2" />
                  Q{currentQuestionIndex + 1}/{totalQuestions}
                </Badge>
              )}
            </div>
          </div>
        </div>
      </nav>

      <main className="relative pt-20 pb-12 px-4 min-h-screen">
        <AnimatePresence mode="wait">
          {/* Loading */}
          {stage === STAGES.LOADING && (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex items-center justify-center min-h-[70vh]"
            >
              <div className="text-center">
                <AIAvatar speaking={false} />
                <div className="mt-8">
                  <Loader2 className="w-8 h-8 text-red-500 animate-spin mx-auto mb-4" />
                  <p className="text-slate-400">Preparing interview room...</p>
                </div>
              </div>
            </motion.div>
          )}

          {/* Credit Check */}
          {stage === STAGES.CREDIT_CHECK && (
            <motion.div
              key="credit"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="max-w-lg mx-auto mt-12"
            >
              <div className="bg-slate-800/80 backdrop-blur-sm rounded-2xl p-8 border border-slate-700/50 shadow-2xl">
                <div className="text-center mb-6">
                  <AIAvatar speaking={false} size="small" />
                  <h2 className="text-2xl font-bold mb-2 mt-6">Interview Credit Required</h2>
                  <p className="text-slate-400">
                    Practice your endorsement interview with AI-powered feedback
                  </p>
                </div>
                
                <div className="bg-slate-900/80 rounded-xl p-6 mb-6 border border-slate-700/50">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-slate-400">Mock Interview Session</span>
                    <span className="text-2xl font-bold text-white">£9.99</span>
                  </div>
                  <ul className="space-y-2 text-sm text-slate-400">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      10 professional interview questions
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      AI interviewer with voice
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      AI scoring & detailed feedback
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      Per-question improvement tips
                    </li>
                  </ul>
                </div>
                
                <Button
                  onClick={purchaseCredit}
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-red-500 to-amber-500 hover:from-red-600 hover:to-amber-600 py-6 text-lg shadow-lg"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  ) : (
                    <Award className="w-5 h-5 mr-2" />
                  )}
                  Purchase Interview Credit
                </Button>
              </div>
            </motion.div>
          )}

          {/* Acknowledgment - Credit Burn Warning */}
          {stage === STAGES.ACKNOWLEDGMENT && (
            <motion.div
              key="acknowledgment"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="max-w-lg mx-auto mt-12"
            >
              <div className="bg-slate-800/80 backdrop-blur-sm rounded-2xl p-8 border border-amber-500/50 shadow-2xl">
                <div className="text-center mb-6">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
                    <AlertTriangle className="w-10 h-10 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold mb-2 text-white">Important Notice</h2>
                  <p className="text-slate-400">
                    Please read carefully before proceeding
                  </p>
                </div>
                
                <div className="bg-amber-500/10 rounded-xl p-6 mb-6 border border-amber-500/30">
                  <h3 className="font-semibold text-amber-400 mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    Credit Policy
                  </h3>
                  <div className="space-y-3 text-sm text-slate-300">
                    <p>
                      <strong className="text-white">Once you start the interview, your credit will be consumed.</strong>
                    </p>
                    <p>
                      If you close the browser, navigate away, or exit the interview before completion, 
                      <span className="text-amber-400 font-semibold"> your credit will NOT be refunded.</span>
                    </p>
                    <p>
                      Please ensure you have:
                    </p>
                    <ul className="list-disc list-inside space-y-1 text-slate-400 ml-2">
                      <li>At least 30 minutes of uninterrupted time</li>
                      <li>A quiet environment for voice recording</li>
                      <li>A stable internet connection</li>
                      <li>Your microphone ready and working</li>
                    </ul>
                  </div>
                </div>

                <div className="bg-slate-900/80 rounded-xl p-4 mb-6 border border-slate-700/50">
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <p className="text-sm text-slate-300">
                      By clicking &quot;I Understand, Proceed&quot; you acknowledge that your interview credit will be used and is non-refundable if you exit early.
                    </p>
                  </div>
                </div>
                
                <div className="flex gap-3">
                  <Button
                    onClick={() => navigate('/dashboard')}
                    variant="outline"
                    className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700"
                  >
                    Go Back
                  </Button>
                  <Button
                    onClick={acknowledgeAndProceed}
                    className="flex-1 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 py-6 text-lg shadow-lg"
                    data-testid="acknowledge-proceed-btn"
                  >
                    <CheckCircle2 className="w-5 h-5 mr-2" />
                    I Understand, Proceed
                  </Button>
                </div>
              </div>
            </motion.div>
          )}

          {/* Mic Test */}
          {stage === STAGES.MIC_TEST && (
            <motion.div
              key="mic"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="max-w-lg mx-auto mt-12"
            >
              <div className="bg-slate-800/80 backdrop-blur-sm rounded-2xl p-8 border border-slate-700/50 shadow-2xl">
                <div className="text-center mb-8">
                  <AIAvatar speaking={false} />
                  <h2 className="text-2xl font-bold mb-2 mt-6">Microphone Test</h2>
                  <p className="text-slate-400">
                    Let&apos;s make sure your microphone is working properly
                  </p>
                </div>
                
                {!micPermission ? (
                  <div className="space-y-4">
                    <div className="bg-amber-500/10 rounded-xl p-4 border border-amber-500/30">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                        <div className="text-sm">
                          <p className="font-medium text-amber-400 mb-1">Microphone Required</p>
                          <p className="text-slate-400">
                            This interview requires voice responses. Please allow microphone access when prompted.
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <Button
                      onClick={requestMicPermission}
                      className="w-full bg-gradient-to-r from-red-500 to-amber-500 hover:from-red-600 hover:to-amber-600 py-6 shadow-lg"
                    >
                      <Mic className="w-5 h-5 mr-2" />
                      Enable Microphone
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-6">
                    <div className="bg-slate-900/80 rounded-xl p-6 border border-slate-700/50">
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-slate-400">Audio Level</span>
                        <CheckCircle2 className="w-5 h-5 text-green-500" />
                      </div>
                      <div className="h-4 bg-slate-700 rounded-full overflow-hidden">
                        <motion.div 
                          className="h-full bg-gradient-to-r from-green-500 to-emerald-400"
                          animate={{ width: Math.min(100, audioLevel) + '%' }}
                          transition={{ duration: 0.1 }}
                        />
                      </div>
                      <p className="text-xs text-slate-500 mt-2">
                        Speak to see the audio level change
                      </p>
                    </div>
                    
                    <div className="bg-green-500/10 rounded-xl p-4 border border-green-500/30">
                      <div className="flex items-center gap-3">
                        <CheckCircle2 className="w-5 h-5 text-green-500" />
                        <span className="text-green-400">Microphone connected and working!</span>
                      </div>
                    </div>
                    
                    <Button
                      onClick={startInterview}
                      disabled={loading}
                      className="w-full bg-gradient-to-r from-red-500 to-amber-500 hover:from-red-600 hover:to-amber-600 py-6 text-lg shadow-lg"
                    >
                      {loading ? (
                        <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      ) : (
                        <Play className="w-5 h-5 mr-2" />
                      )}
                      Start Interview
                    </Button>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Intro Stage */}
          {stage === STAGES.INTRO && (
            <motion.div
              key="intro"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="flex items-center justify-center min-h-[70vh]"
            >
              <div className="text-center max-w-2xl mx-auto">
                <AIAvatar speaking={isSpeaking} />
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="mt-8"
                >
                  <h2 className="text-2xl font-bold mb-2">Meet {interviewerName}</h2>
                  <p className="text-slate-400 mb-6">Your AI Interviewer</p>
                  
                  <div className="bg-slate-800/60 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50">
                    <p className="text-lg text-slate-300 leading-relaxed italic">
                      &ldquo;{interviewerIntro}&rdquo;
                    </p>
                  </div>
                  
                  {isSpeaking && (
                    <div className="mt-6 flex items-center justify-center gap-2 text-red-400">
                      <Volume2 className="w-5 h-5 animate-pulse" />
                      <span className="text-sm">Speaking...</span>
                    </div>
                  )}
                </motion.div>
              </div>
            </motion.div>
          )}

          {/* Reading Phase */}
          {stage === STAGES.READING && currentQuestion && (
            <motion.div
              key="reading"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              className="max-w-6xl mx-auto mt-4 px-4"
            >
              <div className="grid lg:grid-cols-4 gap-6">
                {/* AI Avatar Panel - Compact sidebar */}
                <div className="lg:col-span-1">
                  <div className="bg-slate-800/60 backdrop-blur-sm rounded-2xl p-5 border border-slate-700/50 text-center sticky top-24">
                    <AIAvatar speaking={isSpeaking} size="small" />
                    <h3 className="font-semibold text-white mt-4">{interviewerName}</h3>
                    <p className="text-xs text-slate-400">AI Interviewer</p>
                    
                    {isSpeaking && (
                      <div className="mt-3 flex items-center justify-center gap-2 text-red-400">
                        <Volume2 className="w-4 h-4 animate-pulse" />
                        <span className="text-xs">Speaking...</span>
                      </div>
                    )}
                    
                    <div className="mt-5 text-center">
                      <div className="text-xs text-slate-500 mb-1">Read Time</div>
                      <div className="text-4xl font-bold text-white">{formatTime(readTimer)}</div>
                    </div>
                    
                    <div className="mt-4 pt-4 border-t border-slate-700/50">
                      <div className="text-xs text-slate-500 mb-1">Progress</div>
                      <div className="text-lg font-semibold text-slate-300">
                        {currentQuestionIndex + 1} / {totalQuestions}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Question Panel - Much larger */}
                <div className="lg:col-span-3">
                  <div className="bg-slate-800/60 backdrop-blur-sm rounded-2xl overflow-hidden border border-blue-500/30 shadow-2xl min-h-[70vh] flex flex-col">
                    {/* Header */}
                    <div className="bg-gradient-to-r from-blue-600 to-cyan-500 px-8 py-5">
                      <div className="flex items-center justify-between">
                        <Badge className="bg-white/20 text-white text-sm px-4 py-1.5">
                          {currentQuestion.category}
                        </Badge>
                        <span className="text-white/90 font-medium">
                          Question {currentQuestionIndex + 1} of {totalQuestions}
                        </span>
                      </div>
                    </div>
                    
                    {/* Question - Larger text area */}
                    <div className="flex-1 p-10 flex flex-col justify-center">
                      <h2 className="text-3xl lg:text-4xl font-medium text-white leading-relaxed mb-8">
                        {currentQuestion.question}
                      </h2>
                      
                      <div className="bg-blue-500/10 rounded-xl p-5 border border-blue-500/30 mb-8">
                        <div className="flex items-start gap-4">
                          <Lightbulb className="w-6 h-6 text-blue-400 flex-shrink-0 mt-0.5" />
                          <div className="text-base text-blue-300">
                            <p className="font-medium mb-2">Prepare your answer</p>
                            <p className="text-blue-300/70">
                              Take this time to think about your response. Recording will begin after the countdown.
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Skip Button - Larger */}
                      <Button
                        onClick={skipToAnswer}
                        variant="outline"
                        className="w-full max-w-md mx-auto border-blue-500/50 text-blue-400 hover:bg-blue-500/20 py-4 text-lg"
                        data-testid="skip-to-answer-btn"
                      >
                        <SkipForward className="w-5 h-5 mr-2" />
                        Skip to Answer
                      </Button>
                    </div>
                    
                    {/* Progress Bar */}
                    <div className="px-8 pb-6">
                      <div className="h-3 bg-slate-700 rounded-full overflow-hidden">
                        <motion.div 
                          className="h-full bg-gradient-to-r from-blue-500 to-cyan-400"
                          animate={{ width: ((config?.read_time_seconds - readTimer) / config?.read_time_seconds * 100) + '%' }}
                          transition={{ duration: 0.5 }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Countdown */}
          {stage === STAGES.COUNTDOWN && (
            <motion.div
              key="countdown"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex items-center justify-center min-h-[70vh]"
            >
              <div className="text-center">
                <motion.div
                  key={countdownTimer}
                  initial={{ scale: 0.5, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="w-40 h-40 rounded-full bg-gradient-to-br from-red-500 to-amber-500 flex items-center justify-center mx-auto mb-8 shadow-2xl shadow-red-500/30"
                >
                  <span className="text-7xl font-bold text-white">{countdownTimer}</span>
                </motion.div>
                <p className="text-2xl text-slate-300">Get ready to speak...</p>
                <p className="text-slate-500 mt-2">Recording will begin shortly</p>
              </div>
            </motion.div>
          )}

          {/* Answering Phase */}
          {stage === STAGES.ANSWERING && currentQuestion && (
            <motion.div
              key="answering"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              className="max-w-6xl mx-auto mt-4 px-4"
            >
              <div className="grid lg:grid-cols-4 gap-6">
                {/* Recording Panel - Compact sidebar */}
                <div className="lg:col-span-1">
                  <div className="bg-slate-800/60 backdrop-blur-sm rounded-2xl p-5 border border-red-500/30 text-center sticky top-24">
                    {/* Recording Avatar */}
                    <div className="relative w-24 h-24 mx-auto mb-4">
                      <motion.div
                        className="absolute inset-0 rounded-full bg-red-500/30"
                        animate={{ scale: [1, 1.3, 1], opacity: [0.5, 0, 0.5] }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                      />
                      <div className="relative w-24 h-24 rounded-full bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center">
                        <Mic className="w-10 h-10 text-white" />
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-center gap-2 text-red-400 mb-4">
                      <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                      <span className="text-sm font-medium">RECORDING</span>
                    </div>
                    
                    <div className="text-center">
                      <div className="text-xs text-slate-500 mb-1">Time Remaining</div>
                      <div className="text-4xl font-bold text-white">{formatTime(answerTimer)}</div>
                    </div>
                    
                    <div className="mt-4 pt-4 border-t border-slate-700/50">
                      <div className="text-xs text-slate-500 mb-1">Progress</div>
                      <div className="text-lg font-semibold text-slate-300">
                        {currentQuestionIndex + 1} / {totalQuestions}
                      </div>
                    </div>
                    
                    <Button
                      onClick={manualSubmitAnswer}
                      variant="outline"
                      className="w-full mt-5 border-slate-600 text-slate-300 hover:bg-slate-700 py-3"
                      data-testid="submit-answer-btn"
                    >
                      <ChevronRight className="w-4 h-4 mr-1" />
                      Submit Answer
                    </Button>
                  </div>
                </div>
                
                {/* Answer Panel - Much larger */}
                <div className="lg:col-span-3">
                  <div className="bg-slate-800/60 backdrop-blur-sm rounded-2xl overflow-hidden border border-red-500/50 shadow-2xl shadow-red-500/10 min-h-[70vh] flex flex-col">
                    {/* Header */}
                    <div className="bg-gradient-to-r from-red-600 to-red-500 px-8 py-5">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-3 h-3 rounded-full bg-white animate-pulse" />
                          <span className="text-white font-medium text-lg">Recording in Progress</span>
                        </div>
                        <Badge className="bg-white/20 text-white text-sm px-4 py-1.5">
                          {currentQuestion.category}
                        </Badge>
                      </div>
                    </div>
                    
                    {/* Question & Transcript - Larger area */}
                    <div className="flex-1 p-10 flex flex-col">
                      <div className="mb-6">
                        <p className="text-base text-slate-400 mb-2">Question {currentQuestionIndex + 1}</p>
                        <h2 className="text-2xl lg:text-3xl font-medium text-white leading-relaxed">
                          {currentQuestion.question}
                        </h2>
                      </div>
                      
                      {/* Transcript - Much larger */}
                      <div className="flex-1 bg-slate-900/80 rounded-xl p-6 overflow-y-auto border border-slate-700/50 min-h-[300px]">
                        <div className="flex items-center gap-2 text-slate-500 text-sm mb-4">
                          <User className="w-5 h-5" />
                          <span>Your response (live transcription)</span>
                        </div>
                        <p className="text-lg text-slate-300 leading-relaxed">
                          {transcript || <span className="text-slate-500 italic">Start speaking... your words will appear here</span>}
                        </p>
                      </div>
                    </div>
                    
                    {/* Progress Bar */}
                    <div className="px-8 pb-6">
                      <div className="h-3 bg-slate-700 rounded-full overflow-hidden">
                        <motion.div 
                          className="h-full bg-gradient-to-r from-red-500 to-amber-500"
                          animate={{ width: ((config?.answer_time_seconds - answerTimer) / config?.answer_time_seconds * 100) + '%' }}
                          transition={{ duration: 0.5 }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Processing */}
          {stage === STAGES.PROCESSING && (
            <motion.div
              key="processing"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex items-center justify-center min-h-[70vh]"
            >
              <div className="text-center max-w-md">
                <AIAvatar speaking={isSpeaking} />
                <div className="mt-8">
                  <h2 className="text-2xl font-bold mb-4">Analyzing Your Interview</h2>
                  <p className="text-slate-400 mb-6">
                    {interviewerName} is reviewing your responses and preparing detailed feedback...
                  </p>
                  <div className="flex items-center justify-center gap-3">
                    <Loader2 className="w-6 h-6 text-red-500 animate-spin" />
                    <span className="text-slate-500">This may take a moment</span>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Results */}
          {stage === STAGES.RESULTS && assessment && (
            <motion.div
              key="results"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="max-w-4xl mx-auto mt-8"
            >
              {/* Overall Score Card */}
              <div className="bg-slate-800/80 backdrop-blur-sm rounded-2xl overflow-hidden border border-slate-700/50 mb-6 shadow-2xl">
                <div className="bg-gradient-to-r from-red-500 via-red-600 to-amber-500 px-8 py-8">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-2xl font-bold text-white mb-1">Interview Complete!</h2>
                      <p className="text-white/70">{proposalTitle}</p>
                    </div>
                    <div className="text-center">
                      <div className="text-6xl font-bold text-white">{assessment.overall_score}</div>
                      <div className="text-white/70 text-sm">out of 100</div>
                    </div>
                  </div>
                </div>
                
                <div className="p-6">
                  <div className="flex items-center justify-center mb-6">
                    <Badge className={getReadinessColor(assessment.endorsement_readiness) + ' text-white text-lg px-6 py-2'}>
                      {assessment.endorsement_readiness}
                    </Badge>
                  </div>
                  
                  {/* Category Scores */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {assessment.category_scores && Object.entries(assessment.category_scores).map(([category, score]) => (
                      <div key={category} className="bg-slate-900/60 rounded-xl p-4 text-center border border-slate-700/50">
                        <div className={"text-2xl font-bold " + getScoreColor(score)}>{score}/10</div>
                        <div className="text-sm text-slate-400">{category}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Question-by-Question Feedback */}
              {assessment.question_assessments && assessment.question_assessments.length > 0 && (
                <div className="bg-slate-800/80 backdrop-blur-sm rounded-2xl border border-slate-700/50 mb-6 shadow-xl">
                  <div className="px-6 py-4 border-b border-slate-700/50">
                    <h3 className="text-lg font-bold text-white flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-red-500" />
                      Question-by-Question Feedback
                    </h3>
                  </div>
                  <div className="divide-y divide-slate-700/50">
                    {(() => {
                      const elements = [];
                      for (let i = 0; i < assessment.question_assessments.length; i++) {
                        const qa = assessment.question_assessments[i];
                        elements.push(
                          <div key={i} className="p-6">
                            <div className="flex items-start justify-between mb-3">
                              <span className="text-sm text-slate-400">Question {qa.question_index + 1}</span>
                              <Badge className={getScoreColor(qa.score) + ' bg-transparent border border-current'}>
                                {qa.score}/10
                              </Badge>
                            </div>
                            <p className="text-slate-300 mb-3">{qa.feedback}</p>
                            <div className="bg-slate-900/60 rounded-lg p-3 border border-slate-700/50">
                              <div className="flex items-start gap-2">
                                <Lightbulb className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                                <p className="text-sm text-amber-300/80">{qa.improvement_tip}</p>
                              </div>
                            </div>
                          </div>
                        );
                      }
                      return elements;
                    })()}
                  </div>
                </div>
              )}

              {/* Strengths & Weaknesses */}
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div className="bg-slate-800/80 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6 shadow-xl">
                  <h3 className="text-lg font-bold text-white flex items-center gap-2 mb-4">
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                    Strengths
                  </h3>
                  <ul className="space-y-2">
                    {(() => {
                      const items = [];
                      const strengths = assessment.strengths || [];
                      for (let i = 0; i < strengths.length; i++) {
                        items.push(
                          <li key={i} className="flex items-start gap-2 text-slate-300">
                            <Star className="w-4 h-4 text-green-500 flex-shrink-0 mt-1" />
                            {strengths[i]}
                          </li>
                        );
                      }
                      return items;
                    })()}
                  </ul>
                </div>
                
                <div className="bg-slate-800/80 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6 shadow-xl">
                  <h3 className="text-lg font-bold text-white flex items-center gap-2 mb-4">
                    <Target className="w-5 h-5 text-orange-500" />
                    Areas to Improve
                  </h3>
                  <ul className="space-y-2">
                    {(() => {
                      const items = [];
                      const weaknesses = assessment.weaknesses || [];
                      for (let i = 0; i < weaknesses.length; i++) {
                        items.push(
                          <li key={i} className="flex items-start gap-2 text-slate-300">
                            <AlertTriangle className="w-4 h-4 text-orange-500 flex-shrink-0 mt-1" />
                            {weaknesses[i]}
                          </li>
                        );
                      }
                      return items;
                    })()}
                  </ul>
                </div>
              </div>

              {/* Overall Feedback */}
              <div className="bg-slate-800/80 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6 mb-6 shadow-xl">
                <h3 className="text-lg font-bold text-white flex items-center gap-2 mb-4">
                  <Brain className="w-5 h-5 text-red-500" />
                  Overall Feedback
                </h3>
                <p className="text-slate-300 leading-relaxed whitespace-pre-wrap">
                  {assessment.overall_feedback}
                </p>
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={() => navigate('/dashboard')}
                  variant="outline"
                  className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700"
                >
                  <Home className="w-4 h-4 mr-2" />
                  Back to Dashboard
                </Button>
                <Button
                  onClick={() => window.location.reload()}
                  className="flex-1 bg-gradient-to-r from-red-500 to-amber-500 hover:from-red-600 hover:to-amber-600 shadow-lg"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Practice Again (£9.99)
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}

export default InterviewRoom;
